import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useHybridAuth } from './useHybridAuth';
import type { Rate } from '../lib/calculations';

export type { Rate };

interface RequestParams {
  customerId?: string | null;
  companyId?: string;
}

/**
 * One account's rates, to price an invoice from: the call's account. The
 * server answers this for anyone who may bill that account, which for a
 * driver is the account of a call assigned to them; the whole rates list is
 * "View rates" only, so a driver who invoices never sees another customer's
 * pricing.
 */
export const useGetCustomerRates = (
  { customerId, companyId }: RequestParams,
  config?: { enabled?: boolean }
) => {
  const { getAccessTokenSilently, isAuthenticated } = useHybridAuth();

  return useQuery({
    queryFn: async (): Promise<Rate[]> => {
      const token = await getAccessTokenSilently();
      const headers: Record<string, string> = {
        Authorization: `Bearer ${token}`,
      };
      if (companyId) headers['x-company-id'] = companyId;
      const response = await api.get<{ rates: Rate[] }>(
        `/rates/customer/${customerId}`,
        { headers }
      );
      return response.data.rates ?? [];
    },
    queryKey: ['rates', 'customer', customerId, companyId],
    enabled:
      config?.enabled !== false &&
      isAuthenticated &&
      !!customerId &&
      !!companyId,
  });
};
